/**
 * What the app should show on startup.
 *
 * Pulled out as a pure function because the rules interact in ways that are easy
 * to get subtly, badly wrong. The two that matter:
 *
 *  - Cached pricing, not a valid session, is what lets an estimator work. Losing
 *    a session must never take the bid form away: they may be standing at a
 *    property with no signal and a customer waiting.
 *  - But asking to sign in must always reach the sign-in screen. An earlier
 *    version let cached pricing win unconditionally, which meant a signed-out
 *    estimator could never sign back in, and their queued bids could never sync.
 *    The only escape would have been clearing site data, which would have
 *    deleted those very bids.
 */

export type ServerResult = 'not-attempted' | 'ok' | 'unauthorized' | 'offline' | 'error';

export interface BootInput {
  hasToken: boolean;
  hasCachedPricing: boolean;
  /** The user explicitly asked for the sign-in screen. Always wins. */
  userRequestedLogin: boolean;
  serverResult: ServerResult;
}

export type BootDecision =
  | { kind: 'login'; reason: 'requested' | 'no-session' | 'expired' }
  | { kind: 'ready'; source: 'server' | 'cache'; sessionExpired: boolean }
  | { kind: 'blocked'; reason: 'offline-no-cache' | 'error' };

export function decideBoot(input: BootInput): BootDecision {
  // An explicit request always reaches the sign-in screen, even with pricing
  // cached and even while offline. This is the escape hatch that lets a
  // signed-out estimator get their queued bids synced.
  if (input.userRequestedLogin) return { kind: 'login', reason: 'requested' };

  if (!input.hasToken) {
    return input.hasCachedPricing
      ? { kind: 'ready', source: 'cache', sessionExpired: true }
      : { kind: 'login', reason: 'no-session' };
  }

  switch (input.serverResult) {
    case 'ok':
      return { kind: 'ready', source: 'server', sessionExpired: false };

    case 'unauthorized':
      // The session died, possibly while offline. Keep working locally if we can.
      return input.hasCachedPricing
        ? { kind: 'ready', source: 'cache', sessionExpired: true }
        : { kind: 'login', reason: 'expired' };

    case 'offline':
      return input.hasCachedPricing
        ? { kind: 'ready', source: 'cache', sessionExpired: false }
        : { kind: 'blocked', reason: 'offline-no-cache' };

    case 'error':
    case 'not-attempted':
    default:
      return input.hasCachedPricing
        ? { kind: 'ready', source: 'cache', sessionExpired: false }
        : { kind: 'blocked', reason: 'error' };
  }
}
